/** @format */

import {
	CommonModule,
	DOCUMENT,
	isPlatformBrowser,
	NgClass,
	NgOptimizedImage
} from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	computed,
	effect,
	ElementRef,
	HostBinding,
	inject,
	input,
	OnChanges,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	signal,
	ViewChild
} from '@angular/core';
import { BreakPoint, LoadingStrategy } from '@shared/data-access/enums';
import {
	DeviceService,
	EmitEvent,
	EventBusService,
	Events,
	Scene7Service,
	UtilityService
} from '@shared/data-access/services';
import { chain } from '@core/chain';
import { of, Subject } from 'rxjs';
import { Store as NgRxStore } from '@ngrx/store';
import { PinchToZoomComponent } from '../pinch-to-zoom/pinch-to-zoom.component';
import { selectSelectedColorAttribute } from '@pdp/attributes/data-access';
import { Scene7MediaSetParserService, Scene7MediaSetResponse } from '@shared/media/data-access';
import { ProcessedProduct } from '@shared/data-access/interfaces';
import { HmfMediaSetItem } from '@dsg-tech/homefield-utils';
import { HmfBadgeComponent, HmfButtonComponent } from '@dsg-tech/homefield-angular';
import { OptimizedImageComponentModule } from '@shared/media/components';
import {
	homefieldIconChevronLeft,
	homefieldIconChevronRight,
	HomefieldIconLibraryModule,
	homefieldIconMediaPlay,
	homefieldIconPlMediaPlay,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';
import { ProductAnalyticsService } from '@shared/data-access/analytics';

@Component({
	selector: 'pdp-carousel',
	templateUrl: './carousel.component.html',
	styleUrl: './carousel.component.scss',
	imports: [
		CommonModule,
		HmfBadgeComponent,
		HomefieldIconLibraryModule,
		NgClass,
		NgOptimizedImage,
		OptimizedImageComponentModule,
		PinchToZoomComponent,
		HmfButtonComponent
	]
})
export class CarouselComponent implements OnChanges, OnInit, OnDestroy {
	private readonly document = inject(DOCUMENT);
	private readonly changeDetectorRef = inject(ChangeDetectorRef);
	private readonly deviceService = inject(DeviceService);
	private readonly eventBusService = inject(EventBusService);
	private readonly hostElement = inject(ElementRef);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly scene7Service = inject(Scene7Service);
	private readonly scene7MediaSetParserService = inject(Scene7MediaSetParserService);
	private readonly utilityService = inject(UtilityService);
	private readonly ngrxStore = inject(NgRxStore);
	private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);
	private readonly analyticsService = inject(ProductAnalyticsService);

	readonly available = input(true);
	readonly catalogEntryTypeCode = input<string>();
	readonly eCode = input<string>();
	readonly productName = input<string>();
	readonly swatchColor = input('');
	readonly imageBadge = input<string>();
	readonly lazyLoadImage = input(false);
	readonly customizedUrls = input<string[]>();
	readonly product = input<ProcessedProduct>();
	readonly shouldShowOutOfStockRecs = input(false);
	readonly shouldHaveBorder = input(false);
	readonly alternativeInStockRecsEnabled = input(false);
	readonly mediaSetResponse = input<Scene7MediaSetResponse>();
	readonly isHybridPLA = input(false);
	readonly hybridPLALinkTarget = input<string>();

	private destroy$ = new Subject<void>();

	heroImage: HmfMediaSetItem;

	imageUnavailable = false;

	defaultImage =
		'https://images.dickssportinggoods.com/assets/icons/dsg/ImageUnavailablePDPSwatch.svg';

	currentSelectedAltImage: HmfMediaSetItem;

	readonly altImageActiveIndex = signal(0);

	altImageIndexStart = 1;

	thumbnailGroupIndex = 0;

	altImageIndexEnd;

	altImageMaxDisplay = 0;

	/**
	 * @description
	 * The chain identifier abbreviation
	 */
	chainIdentifierAbbr = chain.chainIdentifierAbbr;

	readonly mediaSetItemCount = signal(0);

	mediaSetItems: HmfMediaSetItem[] = [];

	nearbyStoreCount = 0;
	doesCurrentlySetStoreHaveInventory: boolean;

	private fullScreenLightGalleryInstance: any;

	private mediaSetSuffix = '';

	private swatchColorPrefix = '';

	selectedColorAttribute = this.ngrxStore.selectSignal(selectSelectedColorAttribute);

	imageLoading = false;

	indexOfMedia: number;

	clickTargetOfMedia: string;

	cleanSwatchColor = computed(() => {
		const swatchColor = this.selectedColorAttribute()?.value ?? this.swatchColor();
		return swatchColor?.replace(/[/ -]/g, '_') ?? '';
	});

	cleanImageBadge = computed(() => {
		const badge = this.imageBadge();
		return badge ? this.utilityService.removeWhiteSpace(badge) : badge;
	});

	sizesAttribute = computed(() =>
		this.isHybridPLA() ? '(max-width: 768px) 40vw, 60vw' : '(max-width: 376px) 80vw, 70vw'
	);

	desktopHybridPLADots = computed(() => {
		const dotCount = Math.ceil(this.mediaSetItemCount() / 2);
		const activeDot = Math.floor((this.altImageActiveIndex() + 1) / 2);
		return Array.from({ length: dotCount }, (_, i) => ({
			index: i,
			active: i === activeDot
		}));
	});

	mobileHybridPLADotOffset = computed(() => {
		const isHybridPLA = this.isHybridPLA();
		const itemCount = this.mediaSetItemCount();
		const activeIndex = this.altImageActiveIndex();
		if (!isHybridPLA || itemCount <= 5) {
			return '';
		}
		const dotWidth = 14; // 6px width + 8px gap (`var(--hmf-xxs)`)
		const offsetPx = Math.min(Math.max(0, activeIndex - 2), itemCount - 5) * -dotWidth;
		return `translateX(${offsetPx}px)`;
	});

	@HostBinding('class.hybrid-pla') get hybridPlaClass() {
		return this.isHybridPLA();
	}

	@HostBinding('style.--gallery-column-width') get galleryColumnWidthStyle() {
		return this.isHybridPLA() && this.mediaSetItemCount() > 1 ? 'calc(50% - 0.5rem)' : '100%';
	}

	@ViewChild('pinch') pdpPinchToZoom: PinchToZoomComponent;

	// Ensure handleSwatchColorChange only runs on swatchColor changes after initial setup
	private swatchColorInitialized = false;

	constructor() {
		this.homefieldIconsLibraryService.registerIcons([
			homefieldIconChevronLeft,
			homefieldIconChevronRight,
			homefieldIconMediaPlay,
			homefieldIconPlMediaPlay
		]);

		// Handle color changes
		effect(() => {
			const cleanSwatchColor = this.cleanSwatchColor();
			if (this.swatchColorInitialized) {
				this.handleSwatchColorChange(cleanSwatchColor);
			}
			this.swatchColorInitialized = true;
		});
	}

	ngOnInit(): void {
		this.imageLoading = true;

		if (this.customizedUrls()) {
			this.setCustomizedUrls();
			return;
		}

		// Set the hero image as the default product image while waiting for the media set
		this.setHeroImage();

		// Check the swatch color
		if (typeof this.swatchColor() === 'string' && this.swatchColor().trim() !== '') {
			this.swatchColorPrefix = '_';
		}

		// Check the type code to see if the suffix needs updated
		if (
			typeof this.catalogEntryTypeCode() === 'string' &&
			this.catalogEntryTypeCode().toLowerCase() !== 'bundlebean'
		) {
			this.mediaSetSuffix = '_is';
		}

		this.getMediaSetScene7();
	}

	/**
	 * @description
	 * Angular lifecycle event
	 * NOTE: To test in dev mode manually, use this in console after selecting in-stock-component in the Elements panel:
	 * var inStockComp = ng.getComponent($0); inStockComp.selectedColorAttribute = 'Clay'; ng.markDirty(inStockComp);
	 */
	ngOnChanges(): void {
		if (this.customizedUrls()) {
			this.setCustomizedUrls();
			return;
		}
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	setProductImageData(swatchColor: string) {
		if (isPlatformBrowser(this.platformId)) {
			// Get the image set
			this.scene7Service
				.getProductImageSet$(
					this.eCode(),
					swatchColor,
					this.swatchColorPrefix ? this.swatchColorPrefix : '_',
					this.mediaSetSuffix ? this.mediaSetSuffix : '_is',
					0
				)
				.subscribe({
					next: (mediaSetResponse: Scene7MediaSetResponse) => {
						// Check to ensure there is a media set
						if (!mediaSetResponse?.item) {
							this.setDefaultHeroOnImageUnavailable();
						} else {
							// Parse the Scene7 MediaSet items
							const newMediaSetItems =
								this.scene7MediaSetParserService.parseMediaSetItems(
									Array.isArray(mediaSetResponse.item)
										? mediaSetResponse.item
										: [mediaSetResponse.item]
								);

							// Clear for fresh set of image data, if we don't clear, clicking on a new color attribute
							// can mess up order of new image gallery for that new color, so clear on fresh grab of media images.
							//See: https://dcsgcloud.atlassian.net/browse/PDSL-557
							this.mediaSetItems = [];
							// Run change detection to set the new values
							this.changeDetectorRef.detectChanges();
							// Update the hero image with the first image in the media set
							this.heroImage = newMediaSetItems[0];
							this.mediaSetItems = [...newMediaSetItems];

							this.mediaSetItems.unshift(this.heroImage);

							// Set the media set count, subtract 1 to offset the duplicate item added above
							this.mediaSetItemCount.set(this.mediaSetItems.length - 1);
							// Set the alt image index end
							this.altImageIndexEnd =
								this.altImageMaxDisplay + this.altImageIndexStart;

							// Ensure Product Image Unavailable does not show.
							this.imageUnavailable = false;
						}
					},
					error: () => {
						this.setDefaultHeroOnImageUnavailable();
					}
				});
		}
	}

	/**
	 * @description
	 * Set the hero image as the default product image while waiting for the media set
	 */
	setHeroImage() {
		this.heroImage = {
			src: this.scene7Service.getProductImageUrl(
				this.eCode(),
				this.catalogEntryTypeCode(),
				this.cleanSwatchColor(),
				undefined,
				{ wid: '1400', fmt: 'pjpeg' },
				false
			),
			templateSrc: this.scene7Service.getProductImageUrl(
				this.eCode(),
				this.catalogEntryTypeCode(),
				this.cleanSwatchColor(),
				undefined,
				{},
				false
			),
			type: 'image',
			index: 0
		};
	}

	/**
	 * @description
	 * Click listener for alt images so that the hero image gets updated to the currently selected alt
	 */
	onAltImageClick(index: number) {
		// Ensure there is a valid entry
		if (Array.isArray(this.mediaSetItems) && this.mediaSetItems.length - 1 >= index) {
			// Update the hero image with the alt that was selected
			this.heroImage = this.mediaSetItems[index + 1];

			this.mediaSetItems[0] = this.heroImage;
			// Update currently selected alt image
			this.currentSelectedAltImage = this.heroImage;
			// Update the selected alt index
			this.altImageActiveIndex.set(index);
		}
	}

	private handleSwatchColorChange(swatchColor: string) {
		// Set the active index to 0
		this.altImageActiveIndex.set(0);

		// Clear the lightGallery instance
		this.fullScreenLightGalleryInstance = undefined;

		// Reset position on mobile gallery when the color selection changes
		const gallery = this.document.getElementsByClassName('gallery no-gutters');

		if (gallery?.length) {
			gallery[0].scrollLeft = 0;
		}

		// Set the hero image as the default product image while waiting for the media set
		this.setHeroImage();

		this.setProductImageData(swatchColor);
	}

	// click event for Hero Image
	loadContent(index: number, clickTarget: string, mediaSetType: string) {
		if (this.isHybridPLA() && this.hybridPLALinkTarget()) {
			window.location.href = this.hybridPLALinkTarget();
			return;
		}
		this.indexOfMedia = index;
		this.clickTargetOfMedia = clickTarget;
		// Load the lightGallery stylesheets
		this.loadLightGalleryStyles();
		//open Full Screen Target
		if (this.imageLoading && mediaSetType === 'video') {
			window.requestAnimationFrame(() => this.onFullScreenTargetClick());
			this.imageLoading = false;
		} else {
			this.onFullScreenTargetClick();
		}
	}

	/**
	 * @description
	 * Click listener for targets which initialize full screen mode
	 */
	onFullScreenTargetClick() {
		// Get a reference to the native image element
		const nativeImageElement = this.hostElement.nativeElement.querySelector(
			`#image-viewer-hero-wrapper-${this.eCode()} img`
		);

		// Ensure there is an image
		if (
			typeof nativeImageElement !== 'undefined' &&
			nativeImageElement !== null &&
			typeof this.fullScreenLightGalleryInstance === 'undefined'
		) {
			let lgOptions = {
				autoplay: false,
				autoplayControls: false,
				hash: false,
				share: false,
				speed: 300,
				swipeThreshold: 100,
				mousewheel: false,
				getCaptionFromTitleOrAlt: false,
				download: false,
				autoplayFirstVideo: false,
				actualSize: false,
				closable: false,
				index: this.indexOfMedia || 0,
				loop: true,
				dynamic: true,
				dynamicEl: this.mediaSetItems.slice(1, this.mediaSetItems.length),
				hideBarsDelay: 600000
			};

			// Check the viewport width
			if (this.deviceService.getDeviceBreakPoint() > BreakPoint.SMALL) {
				// This is expensive! Only calling directly and forcing recalculate because it is on a click event and not page load or window resize!
				const viewportDimensions = this.deviceService.getDeviceViewportDimensions(true);

				lgOptions = Object.assign(
					{
						thumbnail: true,
						thumbWidth: viewportDimensions.width / 6,
						thumbContHeight: viewportDimensions.height / 5,
						thumbMargin: 10
					},
					lgOptions
				);
			}

			(($) => {
				// Only create a new instance if one doesn't already exist
				if (!this.fullScreenLightGalleryInstance) {
					// Create the lightGallery Class instance
					this.fullScreenLightGalleryInstance =
						$(nativeImageElement).lightGallery(lgOptions);

					// Register the close Event - moved inside instance creation check to prevent multiple handlers
					this.fullScreenLightGalleryInstance.on('onCloseAfter.lg', () => {
						// Check if the instance exists and has the off method before trying to unregister events
						if (
							this.fullScreenLightGalleryInstance &&
							typeof this.fullScreenLightGalleryInstance.off === 'function'
						) {
							// Un-register the close Event
							this.fullScreenLightGalleryInstance.off('onCloseAfter.lg');

							// Un-register the after slide Event
							this.fullScreenLightGalleryInstance.off('onAfterSlide.lg');
						}

						// Check if the instance exists and has the data method before trying to destroy
						if (
							this.fullScreenLightGalleryInstance &&
							typeof this.fullScreenLightGalleryInstance.data === 'function'
						) {
							// Destroy the lightGallery jQuery instance
							const lgInstance =
								this.fullScreenLightGalleryInstance.data('lightGallery');
							if (lgInstance && typeof lgInstance.destroy === 'function') {
								lgInstance.destroy(true);
							}
						}

						// Reset the lightGallery Class reference
						this.fullScreenLightGalleryInstance = undefined;
					});
				}
			})(jQuery);
		}
	}

	/**
	 * @description
	 * Loads the lightGallery plugin stylesheets
	 */
	private loadLightGalleryStyles(): void {
		// Load the main lightGallery stylesheet
		this.utilityService.loadStyleSheet(
			'assets/plugins/lightgallery/css/lightgallery.min.css',
			'lg-main'
		);

		// Load the lightGallery transitions stylesheet
		this.utilityService.loadStyleSheet(
			'assets/plugins/lightgallery/css/lg-transitions.min.css',
			'lg-transitions'
		);
	}

	toggleStoreLayerSideNav() {
		this.eventBusService.emit(new EmitEvent(Events.ChangeStore));
	}

	/**
	 * @description
	 * get media set items using scene 7
	 */
	private getMediaSetScene7() {
		// Get the image set
		const getProductImageSet$ =
			this.mediaSetResponse() !== undefined
				? of(this.mediaSetResponse())
				: this.scene7Service.getProductImageSet$(
						this.eCode(),
						this.cleanSwatchColor(),
						this.swatchColorPrefix,
						this.mediaSetSuffix,
						0
					);

		getProductImageSet$.subscribe({
			next: (mediaSetResponse: Scene7MediaSetResponse) => {
				// Check to ensure there is a media set
				if (typeof mediaSetResponse?.item !== 'undefined') {
					// Parse the Scene7 MediaSet items
					this.mediaSetItems = this.scene7MediaSetParserService.parseMediaSetItems(
						Array.isArray(mediaSetResponse.item)
							? mediaSetResponse.item
							: [mediaSetResponse.item]
					);
					this.changeDetectorRef.detectChanges();

					// debug media set items
					// Update the hero image with the first image in the media set
					this.heroImage = this.mediaSetItems[0];

					this.mediaSetItems.unshift(this.heroImage);

					// Set the media set count, subtract 1 to offset the duplicate item added above
					this.mediaSetItemCount.set(this.mediaSetItems.length - 1);

					// Set the alt image index end
					this.altImageIndexEnd = this.altImageMaxDisplay + this.altImageIndexStart;

					if (this.heroImage && this.eCode()) {
						this.analyticsService.sendAttentiveHeroImage(
							this.heroImage.src,
							this.eCode()
						);
					}
				} else {
					this.setDefaultHeroOnImageUnavailable();
				}
			},
			error: () => {
				this.setDefaultHeroOnImageUnavailable();
			}
		});
	}

	setImageAsPriority(index: number): boolean {
		return !this.lazyLoadImage() && index === 0;
	}

	setLoadingStrategy(index: number): LoadingStrategy {
		return index === 0 ? LoadingStrategy.Eager : LoadingStrategy.Lazy;
	}

	setDefaultHeroOnImageUnavailable(): void {
		this.mediaSetItems = [];
		this.heroImage = {
			poster: this.defaultImage,
			templateSrc: this.defaultImage
		};
		this.imageUnavailable = true;
	}

	private setCustomizedUrls() {
		this.mediaSetItems = [];
		for (let i = 0; i < this.customizedUrls().length; ++i) {
			this.mediaSetItems.push({
				src: this.customizedUrls()[0],
				templateSrc: this.customizedUrls()[0],
				thumb: this.customizedUrls()[0],
				type: 'image',
				index: i
			});
		}

		this.heroImage = {
			src: this.customizedUrls()[0],
			templateSrc: this.customizedUrls()[0],
			type: 'image',
			index: 0
		};

		this.mediaSetItems.unshift(this.heroImage);

		// Set the media set count, subtract 1 to offset the duplicate item added above
		this.mediaSetItemCount.set(this.mediaSetItems.length - 1);
	}

	/**
	 * @description
	 * This handles the swipe on the gallery component as scroll event and aligns the image indicators with the images in mobile view.
	 */
	handleGalleryScroll(target: EventTarget) {
		const galleryItemElements = document.querySelectorAll<HTMLElement>('.gallery-item');
		const galleryItemsPositions = [];
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		galleryItemElements.forEach((item, idx) => {
			galleryItemsPositions.push(item.offsetLeft);
		});
		const currentElementPosition = (target as HTMLElement).scrollLeft;

		let newActiveIndex = this.altImageActiveIndex();
		galleryItemsPositions.forEach((itemLeft, idx) => {
			if (itemLeft <= currentElementPosition) {
				newActiveIndex = idx;
			}
		});
		this.altImageActiveIndex.set(newActiveIndex);
		this.heroImage = this.mediaSetItems[newActiveIndex + 1];
		this.mediaSetItems[0] = this.heroImage;
		this.currentSelectedAltImage = this.heroImage;
	}

	// If an individual image in the media set errors, replace that image with the Image Unavailable image
	handleImageError(imageIndex: number) {
		// just templateSrc is used in carousel, everything else is needed for lightgallery
		this.mediaSetItems[imageIndex].src = this.defaultImage;
		this.mediaSetItems[imageIndex].srcset = this.defaultImage;
		this.mediaSetItems[imageIndex].templateSrc = this.defaultImage;
		this.mediaSetItems[imageIndex].thumb = this.defaultImage;
	}

	scrollCarousel(toRight = true) {
		let newIndex = this.altImageActiveIndex();
		if (toRight) {
			// Move right 2 images, but don't show an empty space if we have an odd # of images
			newIndex = Math.min(this.mediaSetItemCount() - 2, newIndex + 2);
		} else {
			// Move left 2 images
			newIndex = Math.max(0, newIndex - 2);
		}

		const galleryElement: HTMLDivElement =
			this.hostElement.nativeElement.querySelector('.gallery');
		const galleryItemElements =
			galleryElement.querySelectorAll<HTMLDivElement>('.gallery-item');

		const targetScrollPoint = galleryItemElements[newIndex]?.offsetLeft || 0;

		galleryElement.scrollTo({
			left: targetScrollPoint
		});
	}
}
